import * as React from 'react';

import { DescriptionPage_SelfServeConfiguration_AboutModule_SubsectionName as AboutModuleSubsectionName } from '__generated__/graphql-types';

import { useIsEnterpriseAdminAbleToAssignContent } from 'bundles/enterprise-admin/useIsEnterpriseAdminAbleToAssignContent';
import LearnerIncentiveModuleRenderer from 'bundles/unified-description-page-common/module-renderers/LearnerIncentiveModuleRenderer';
import PageBannerModuleRenderer from 'bundles/unified-description-page-common/module-renderers/PageBannerModuleRenderer';
import ProjectInstructionsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/ProjectInstructionsModuleRenderer';
import ReviewsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/ReviewsModuleRenderer';
import TestimonialsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/TestimonialsModuleRenderer';
import ThinBannerModuleRenderer from 'bundles/unified-description-page-common/module-renderers/ThinBannerModuleRenderer';
import ConsumerBreadcrumbsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/breadcrumbs/ConsumerBreadcrumbsModuleRenderer';
import CareerOutcomesModuleRenderer from 'bundles/unified-description-page-common/module-renderers/career-outcomes/ProjectCareerOutcomesModuleRenderer';
import FAQsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/faqs/ProjectFAQsModuleRenderer';
import ProductOverviewModuleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/ConsumerProjectProductOverviewModuleRenderer';
import ProjectContentModuleRenderer from 'bundles/unified-description-page-common/module-renderers/project-content/ProjectContentModuleRenderer';
import ConsumerProjectRecommendationsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/recommendations/ConsumerProjectRecommendationsModuleRenderer';
import UpsellModuleRenderer from 'bundles/unified-description-page-common/module-renderers/upsell/ConsumerUpsellModuleRenderer';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import {
  KeyInformationItemNames,
  ModuleNames,
  ProductDetailItemNames,
  UpsellCardNames,
} from 'bundles/unified-description-page-common/types/moduleConfig';
import type {
  PageConfig,
  ProductOverviewModuleConfig,
} from 'bundles/unified-description-page-common/types/moduleConfig';
import { getAudienceLanguageCode } from 'bundles/unified-description-page-common/utils/dubbingUtil';

import ProjectsHeroImage from 'images/unified-description-page/projects-hero-image.png';

type Props = {
  pageData: ConsumerCoursePageFieldsFragment;
  pageConfig?: PageConfig | null;
};

const Renderer = ({ pageData, pageConfig }: Props) => {
  const isEnterpriseAdminAbleToAssignContent = useIsEnterpriseAdminAbleToAssignContent({ ssr: false });

  if (!pageConfig || !pageConfig.modules.length) {
    return null;
  }

  const hasShareableCertificate = pageData.certificates && pageData.certificates.length;

  const modulesToRender = pageConfig.modules.map((moduleConfig) => {
    switch (moduleConfig.name) {
      case ModuleNames.ProductOverview:
        return (
          <ProductOverviewModuleRenderer
            pageData={pageData}
            config={
              {
                ...moduleConfig,
                // visibility of hero language info depends on XDP product AI dubbing, user site language
                showHeroLanguageInfo:
                  pageData.dubbedLanguages?.length && getAudienceLanguageCode(pageData.dubbedLanguages),
                backgroundImage: ProjectsHeroImage,
                showAssignButton: isEnterpriseAdminAbleToAssignContent,
                keyInfoModule: {
                  items: [
                    { name: KeyInformationItemNames.ProductType },
                    { name: KeyInformationItemNames.Reviews },
                    { name: KeyInformationItemNames.Difficulty },
                    { name: KeyInformationItemNames.Duration },
                    { name: KeyInformationItemNames.LearningMethod },
                    { name: KeyInformationItemNames.EarnCertificate },
                  ],
                },
                aboutModule: {
                  subsections: [
                    { name: AboutModuleSubsectionName.LearningOutcomes },
                    { name: AboutModuleSubsectionName.Skills },
                    { name: AboutModuleSubsectionName.DetailsToKnow },
                  ],
                },
                productDetailItems: [
                  hasShareableCertificate ? ProductDetailItemNames.ShareableCertificate : null,
                  ProductDetailItemNames.LanguagesWithMLInfo,
                  ProductDetailItemNames.RecentlyUpdated,
                  ProductDetailItemNames.InstallationRequirements,
                ].filter(Boolean),
              } as ProductOverviewModuleConfig
            }
            key={moduleConfig.name}
          />
        );
      case ModuleNames.CareerOutcomes:
        return <CareerOutcomesModuleRenderer key={moduleConfig.name} />;
      case ModuleNames.LearnerIncentive:
        return <LearnerIncentiveModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Syllabus:
        return <ProjectContentModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Reviews:
        return <ReviewsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Testimonials:
        return <TestimonialsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Recommendations:
        return <ConsumerProjectRecommendationsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Upsell:
        return (
          <UpsellModuleRenderer
            key={moduleConfig.name}
            config={{
              upsellCards: [
                UpsellCardNames.CourseraPlusUpsellCard,
                UpsellCardNames.DegreeUpsellCard,
                UpsellCardNames.EnterpriseUpsellCard,
              ],
            }}
          />
        );
      case ModuleNames.Faqs:
        return <FAQsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.ThinBanner:
        return <ThinBannerModuleRenderer key={moduleConfig.name} />;
      case ModuleNames.PageBanner:
        return <PageBannerModuleRenderer key={moduleConfig.name} />;
      case ModuleNames.Breadcrumbs:
        return <ConsumerBreadcrumbsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.ProjectInstructions:
        return <ProjectInstructionsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      default:
        return null;
    }
  });

  return <>{modulesToRender}</>;
};

export default Renderer;
